import axios from 'axios';
import { GraphQLFetchPolicy } from '@getaccept/lib-shared/src/graphql/graphql-fetch-policy';
import type { Entity } from '@getaccept/lib-shared/src/types/entity';
import bugsnagClient from '@getaccept/lib-shared/src/bugsnag';
import {
  addons as addonsQuery,
  entity as entityQuery,
  securitySettings as securitySettingsQuery,
  dataRemovalSettings as dataRemovalSettingsQuery,
  dataProtectionSettings as dataProtectionSettingsQuery,
} from '@getaccept/lib-shared/src/entity/entity.queries';
import type { Addon } from '@getaccept/lib-shared/src/types/addon';
import { apolloClientInternal } from '@getaccept/lib-shared/src/internal/apollo-client-internal';
import type { UpdateEntityInput } from '../types/update-entity-input';
import { EntityMutations } from '../graphql/entity.mutations';
import type { DocumentsPendingRemoval } from '../types/documents-pending-removal';
import type { DataRemovalSettings } from '../types/data-removal-settings';
import type { DocumentRemovalSettings } from '../types/document-removal-settings';
import type { DataProtectionSettings } from '../types/data-protection-settings';
import type { SecuritySettings } from '../types/security-settings';

export class EntityService {
  static async fetchEntity(): Promise<Entity> {
    const {
      data: { entity },
    } = await apolloClientInternal.query({
      query: entityQuery,
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return entity;
  }

  static async fetchSecuritySettings(): Promise<SecuritySettings> {
    const {
      data: { entity },
    } = await apolloClientInternal.query({
      query: securitySettingsQuery,
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    const securitySettings = { ...entity };
    delete securitySettings.__typename;

    return securitySettings;
  }

  static async fetchAddons(): Promise<Addon[]> {
    const {
      data: { addons },
    } = await apolloClientInternal.query({
      query: addonsQuery,
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return addons;
  }

  static async saveSetting(entityId: string, fieldName: string, value: boolean | number | string) {
    const data = new FormData();
    data.append('action', 'entity');
    data.append('id', entityId);
    data.append('field', fieldName);
    if (typeof value === 'boolean') {
      data.append('value', value ? '1' : '0');
    } else {
      data.append('value', value.toString());
    }
    const {
      data: { error },
    } = await axios.post('/save/settings', data);
    if (error) {
      if (bugsnagClient) {
        bugsnagClient.notify(error);
      }
      throw new Error(error);
    }
  }

  static async getDocumentsPendingRemoval(
    entityId,
    documentRemovalSettings: DocumentRemovalSettings
  ): Promise<DocumentsPendingRemoval> {
    const {
      removeInactive,
      removeInactiveMonths,
      removeSigned,
      removeSignedMonths,
    } = documentRemovalSettings;
    const formData = new FormData();

    formData.append('action', 'check-data-protection');
    formData.append('id', entityId);
    formData.append('entity-remove-inactive', removeInactive.toString());
    formData.append('entity-remove-inactive-months', removeInactiveMonths.toString());
    formData.append('entity-remove-signed', removeSigned.toString());
    formData.append('entity-remove-signed-months', removeSignedMonths.toString());

    const { data } = await axios.post('/save/settings', formData);
    if (data?.error) {
      if (bugsnagClient) {
        bugsnagClient.notify(data?.error);
      }
      throw new Error(data?.error);
    }
    return {
      signedDocuments: data.signed_documents,
      inactiveDocuments: data.inactive_documents,
    };
  }

  static async update(input: UpdateEntityInput): Promise<boolean> {
    const {
      data: { updateEntity },
    } = await apolloClientInternal.mutate({
      mutation: EntityMutations.update,
      variables: {
        input,
      },
    });
    return updateEntity;
  }

  static async fetchDataRemovalSettings(): Promise<DataRemovalSettings> {
    const {
      data: { entity: dataRemovalSettings },
    } = await apolloClientInternal.query({
      query: dataRemovalSettingsQuery,
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return dataRemovalSettings;
  }

  static async fetchDataProtectionSettings(): Promise<DataProtectionSettings> {
    const {
      data: { entity },
    } = await apolloClientInternal.query({
      query: dataProtectionSettingsQuery,
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    const dataProtectionSettings = { ...entity };
    delete dataProtectionSettings.__typename;
    return dataProtectionSettings;
  }

  static async loadEntityOld(): Promise<{
    emailSendTemplateId: string;
    emailNotificationTemplateId: string;
  }> {
    const {
      data: { entity },
    } = await axios.get('/get/settings/entity');
    return {
      emailSendTemplateId: entity.email_send_template_id,
      emailNotificationTemplateId: entity.email_notification_template_id,
    };
  }
}
